import { MouseEvent, useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import useAuth from 'hooks/query/auth/useAuth'
import useLogout from 'hooks/query/auth/useLogout'
import BrwnieLogo from './BrwnieLogo'
import { ASSET, LOGIN_PATH, renewalPath } from './paths'
import { INDUSTRIES, RenewalCurrent, SERVICES, industryIcon } from './nav'

type MenuKey = 'ind' | 'svc' | 'acct' | null

type RenewalHeaderProps = {
  current?: RenewalCurrent
  industryKey?: string
}

const MAIN_LINKS: { key: RenewalCurrent; label: string; path: string }[] = [
  { key: 'cases', label: '관리 사례', path: renewalPath('/cases') },
  { key: 'pricing', label: '이용요금', path: renewalPath('/pricing') },
  { key: 'business', label: '프랜차이즈 본사', path: renewalPath('/business') },
  { key: 'shop', label: '소모품 구매', path: renewalPath('/shop') },
  { key: 'faq', label: 'FAQ', path: renewalPath('/faq') },
]

const ACCOUNT_LINKS: { key: RenewalCurrent; label: string; path: string }[] = [
  { key: 'mypage', label: '마이페이지', path: '/mypage' },
  { key: 'myservice', label: '서비스 신청·관리', path: '/store' },
  { key: 'payment', label: '결제 내역', path: '/payment' },
]

const SERVICE_KEYS = SERVICES.map((item) => item.key)

export default function RenewalHeader({ current = 'home', industryKey }: RenewalHeaderProps) {
  const history = useHistory()
  const { isLogin, auth } = useAuth()
  const { mutateAsync: logout } = useLogout()
  const mobile = useMaxWidth(1260)
  const [open, setOpen] = useState(false)
  const [menu, setMenu] = useState<MenuKey>(null)
  const [mobileSub, setMobileSub] = useState<Record<string, boolean>>({})
  const hoverAt = useRef(0)
  const userName = auth?.real_name || ''

  const openMenu = (key: MenuKey) => {
    setMenu((value) => {
      if (value !== key) hoverAt.current = Date.now()
      return key
    })
  }

  const toggleMenu = (key: Exclude<MenuKey, null>) => {
    if (Date.now() - hoverAt.current < 400) return
    setMenu((value) => (value === key ? null : key))
  }

  useEffect(() => {
    setOpen(false)
    setMenu(null)
  }, [history.location.pathname, mobile])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenu(null)
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const goHomeTop = (event: MouseEvent<HTMLAnchorElement>) => {
    setMenu(null)
    setOpen(false)
    const home = renewalPath('/')
    const here = history.location.pathname
    const onHome = here === home || here === `${home}/`
    if (!onHome) return
    event.preventDefault()
    document.querySelector('.renewal-root')?.scrollTo({ top: 0, behavior: 'smooth' })
    if (history.location.hash) history.push(home)
  }

  const onNavClick = (event: MouseEvent<HTMLAnchorElement>) => {
    setMenu(null)
    setOpen(false)
    const href = event.currentTarget.getAttribute('href') || ''
    const path = href.split(/[?#]/)[0]
    const here = history.location.pathname
    const same = path === here || path === `${here}/` || `${path}/` === here
    if (!same) return
    event.preventDefault()
    document.querySelector('.renewal-root')?.scrollTo({ top: 0, behavior: 'smooth' })
    if (history.location.hash) history.replace({ pathname: here, search: history.location.search })
  }

  const onLogout = async () => {
    try {
      await logout()
    } catch {
      // 세션 삭제가 실패해도 리뉴얼 홈으로 보낸다.
    }
    setOpen(false)
    setMenu(null)
    history.push(renewalPath('/'))
  }

  const industryOn = current === 'industry'
  const serviceOn = SERVICE_KEYS.includes(current)

  return (
    <Header
      onMouseLeave={() => {
        if (!mobile) setMenu(null)
      }}
    >
      <Bar>
        <Logo to={renewalPath('/')} aria-label="브라우니 홈" onMouseEnter={() => setMenu(null)} onClick={goHomeTop}>
          <BrwnieLogo />
        </Logo>

        <Nav aria-label="주 메뉴">
              <MenuButton
                type="button"
                $active={industryOn}
                $open={menu === 'ind'}
                aria-expanded={menu === 'ind'}
                aria-controls="ind-menu"
                onMouseEnter={() => openMenu('ind')}
                onClick={() => toggleMenu('ind')}
              >
                업종별 관리
                <Caret $open={menu === 'ind'} />
              </MenuButton>
              <TextLink to={renewalPath('/cases')} $active={current === 'cases'} onMouseEnter={() => setMenu(null)} onClick={onNavClick}>
                관리 사례
              </TextLink>
              <TextLink to={renewalPath('/pricing')} $active={current === 'pricing'} onMouseEnter={() => setMenu(null)} onClick={onNavClick}>
                이용요금
              </TextLink>
              <MenuButton
                type="button"
                $active={serviceOn}
                $open={menu === 'svc'}
                aria-expanded={menu === 'svc'}
                aria-controls="svc-menu"
                onMouseEnter={() => openMenu('svc')}
                onClick={() => toggleMenu('svc')}
              >
                함께하는 서비스
                <Caret $open={menu === 'svc'} />
              </MenuButton>
              {MAIN_LINKS.slice(2).map((item) => (
                <TextLink key={item.key} to={item.path} $active={current === item.key} onMouseEnter={() => setMenu(null)} onClick={onNavClick}>
                  {item.label}
                </TextLink>
              ))}
            </Nav>
            <Account onMouseEnter={() => setMenu(null)}>
              {isLogin ? (
                <>
                  <AccountBox>
                    <NameButton
                      type="button"
                      $open={menu === 'acct'}
                      aria-expanded={menu === 'acct'}
                      aria-controls="acct-menu"
                      onMouseEnter={() => openMenu('acct')}
                      onClick={() => toggleMenu('acct')}
                    >
                      <Avatar />
                      <span>{userName} 님</span>
                      <Caret $open={menu === 'acct'} />
                    </NameButton>
                    <AccountDrop id="acct-menu" $open={menu === 'acct'}>
                      <AccountList aria-label="내 계정">
                        {ACCOUNT_LINKS.map((item) => (
                          <AccountItem key={item.key}>
                            <AccountLink to={item.path} $on={current === item.key} onClick={onNavClick}>
                              {item.label}
                            </AccountLink>
                          </AccountItem>
                        ))}
                      </AccountList>
                    </AccountDrop>
                  </AccountBox>
                  <LoginButton type="button" onClick={onLogout} onMouseEnter={() => setMenu(null)}>
                    로그아웃
                  </LoginButton>
                </>
              ) : (
                <LoginButton as={Link} to={LOGIN_PATH}>
                  로그인
                </LoginButton>
              )}
            </Account>
        <Hamburger type="button" aria-expanded={open} aria-label={open ? '메뉴 닫기' : '메뉴 열기'} onClick={() => setOpen((value) => !value)}>
          {open ? <CloseIcon /> : <MenuIcon />}
        </Hamburger>
      </Bar>

      {!mobile && (
        <>
          <Mega id="ind-menu" $open={menu === 'ind'} aria-hidden={menu !== 'ind'}>
            <MegaInner>
              <MegaIntro>
                <Eyebrow>업종별 관리</Eyebrow>
                <MegaTitle>
                  업종마다
                  <br />
                  챙기는 일이 달라요
                </MegaTitle>
                <OverviewLink to={renewalPath('/industries')} onClick={onNavClick}>
                  업종 한눈에 보기
                  <Chevron />
                </OverviewLink>
              </MegaIntro>
              <IndustryGrid>
                {INDUSTRIES.map((item) => (
                  <IndustryLink key={item.key} to={item.path} $on={industryKey === item.key} onClick={onNavClick}>
                    <img src={industryIcon(item.key)} alt="" />
                    <span>{item.name}</span>
                  </IndustryLink>
                ))}
              </IndustryGrid>
            </MegaInner>
          </Mega>
          <Mega id="svc-menu" $open={menu === 'svc'} aria-hidden={menu !== 'svc'}>
            <MegaInner>
              <MegaIntro>
                <Eyebrow>함께하는 서비스</Eyebrow>
                <MegaTitle>
                  매장 관리와
                  <br />
                  함께 맡겨 보세요
                </MegaTitle>
              </MegaIntro>
              <ServiceGrid>
                {SERVICES.map((item, index) => (
                  <ServiceLink key={item.key} to={item.path} $on={current === item.key} onClick={onNavClick}>
                    <ServiceTop>
                      <IconBox>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
                          <path d={item.icon} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </IconBox>
                      <Num>{String(index + 1).padStart(2, '0')}</Num>
                    </ServiceTop>
                    <ServiceName>
                      <ServiceLabel>{item.name}</ServiceLabel>
                      <ServiceChevron width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#003399" strokeWidth="2.4" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                      </ServiceChevron>
                    </ServiceName>
                  </ServiceLink>
                ))}
              </ServiceGrid>
            </MegaInner>
          </Mega>
        </>
      )}

      {mobile && open && (
        <MobilePanel>
          <MobileNav aria-label="모바일 메뉴">
            {isLogin && (
              <>
                <MobileNameButton type="button" aria-expanded={!!mobileSub.acct} onClick={() => setMobileSub((value) => ({ ...value, acct: !value.acct }))}>
                  <NameRow>
                    <Avatar size={32} />
                    <span>{userName} 님</span>
                  </NameRow>
                  <Caret $open={!!mobileSub.acct} $mobile />
                </MobileNameButton>
                {mobileSub.acct && (
                  <MobileAccount aria-label="내 계정">
                    {ACCOUNT_LINKS.map((item) => (
                      <AccountItem key={item.key}>
                        <MobileAccountLink to={item.path} $on={current === item.key} onClick={onNavClick}>
                          {item.label}
                        </MobileAccountLink>
                      </AccountItem>
                    ))}
                  </MobileAccount>
                )}
              </>
            )}
            <SubButton type="button" $active={industryOn} aria-expanded={!!mobileSub.ind} onClick={() => setMobileSub((value) => ({ ...value, ind: !value.ind }))}>
              업종별 관리
              <Caret $open={!!mobileSub.ind} $mobile />
            </SubButton>
            {mobileSub.ind && (
              <ChipGrid>
                <ChipItem>
                  <ChipLink to={renewalPath('/industries')} onClick={onNavClick}>
                    <span>업종 한눈에 보기</span>
                  </ChipLink>
                </ChipItem>
                {INDUSTRIES.map((item) => (
                  <ChipItem key={item.key}>
                    <ChipLink to={item.path} $on={industryKey === item.key} onClick={onNavClick}>
                      <img src={industryIcon(item.key)} alt="" />
                      <span>{item.name}</span>
                    </ChipLink>
                  </ChipItem>
                ))}
              </ChipGrid>
            )}
            <MobileLink to={renewalPath('/cases')} $active={current === 'cases'} onClick={onNavClick}>
              관리 사례
              <MobileArrow />
            </MobileLink>
            <MobileLink to={renewalPath('/pricing')} $active={current === 'pricing'} onClick={onNavClick}>
              이용요금
              <MobileArrow />
            </MobileLink>
            <SubButton type="button" $active={serviceOn} aria-expanded={!!mobileSub.svc} onClick={() => setMobileSub((value) => ({ ...value, svc: !value.svc }))}>
              함께하는 서비스
              <Caret $open={!!mobileSub.svc} $mobile />
            </SubButton>
            {mobileSub.svc && (
              <ChipGrid>
                {SERVICES.map((item) => (
                  <ChipItem key={item.key}>
                    <ChipLink to={item.path} $on={current === item.key} $fill="#f7f8fa" onClick={onNavClick}>
                      <span>{item.name}</span>
                    </ChipLink>
                  </ChipItem>
                ))}
              </ChipGrid>
            )}
            <MobileLink to={renewalPath('/business')} $active={current === 'business'} onClick={onNavClick}>
              프랜차이즈 본사
              <MobileArrow />
            </MobileLink>
            <MobileLink to={renewalPath('/shop')} $active={current === 'shop'} onClick={onNavClick}>
              소모품 구매
              <MobileArrow />
            </MobileLink>
            <MobileLink to={renewalPath('/faq')} $active={current === 'faq'} onClick={onNavClick}>
              FAQ
              <MobileArrow />
            </MobileLink>
            {isLogin ? (
              <LoginButton type="button" onClick={onLogout}>
                로그아웃
              </LoginButton>
            ) : (
              <LoginButton as={Link} to={LOGIN_PATH}>
                로그인
              </LoginButton>
            )}
          </MobileNav>
        </MobilePanel>
      )}
    </Header>
  )
}

function useMaxWidth(px: number) {
  const query = `(max-width: ${px}px)`
  const [matches, setMatches] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false))

  useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = () => setMatches(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [query])

  return matches
}

function Caret({ $open, $mobile = false }: { $open: boolean; $mobile?: boolean }) {
  return (
    <CaretSvg
      $open={$open}
      width={$mobile ? 18 : 14}
      height={$mobile ? 18 : 14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={$mobile ? '#8B95A1' : 'currentColor'}
      strokeWidth={$mobile ? 2.2 : 2.6}
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </CaretSvg>
  )
}

function MobileArrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B95A1" strokeWidth="2.2" aria-hidden="true">
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Chevron() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Avatar({ size = 30 }: { size?: number }) {
  return (
    <AvatarCircle $size={size} aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 12a4 4 0 1 0 0-8a4 4 0 1 0 0 8zM4.5 20.5c1.3-3.5 4.2-5.5 7.5-5.5s6.2 2 7.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </AvatarCircle>
  )
}

function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  )
}

const Header = styled.header`
  position: relative;
  background: rgba(255, 255, 255, 0.97);
  border-bottom: 1px solid #e5e8eb;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  word-break: keep-all;
  color: #191f28;
`

const Bar = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  min-height: 68px;
  padding: 0 clamp(20px, 3.4vw, 40px);
  display: flex;
  align-items: center;
  gap: 20px;
`

const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  flex-shrink: 0;

  svg {
    height: 24px;
    width: auto;
    display: block;
  }
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 1260px) {
    display: none;
  }
`

const TextLink = styled(Link)<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  line-height: inherit;
  color: ${({ $active }) => ($active ? '#003399' : '#333d4b')};
  white-space: nowrap;
  transition: background-color 0.2s;

  &:hover {
    background: #f2f4f6;
  }
`

const MenuButton = styled.button<{ $active?: boolean; $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  padding: 0 8px 0 10px;
  border: 0;
  border-radius: 10px;
  background: ${({ $open }) => ($open ? '#f2f4f6' : 'transparent')};
  font-family: inherit;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  line-height: inherit;
  color: ${({ $active }) => ($active ? '#003399' : '#333d4b')};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f2f4f6;
  }
`

const CaretSvg = styled.svg<{ $open: boolean }>`
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.25s;
`

const Account = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 1260px) {
    display: none;
  }
`

const AccountBox = styled.div`
  position: relative;
`

const AccountDrop = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 14px;
  width: max-content;
  z-index: 5;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-8px)')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease, transform 0.25s ease, visibility 0.2s;
`

const AccountList = styled.ul`
  margin: 0;
  padding: 8px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #ffffff;
  border: 1px solid #e5e8eb;
  border-radius: 16px;
  box-shadow: 0 24px 40px -24px rgba(12, 32, 73, 0.4);
`

const AccountItem = styled.li`
  display: flex;
`

const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin-top: 0;
  padding: 0 18px;
  border: 0;
  border-radius: 12px;
  background: #003399;
  color: #ffffff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  line-height: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #002a80;
    color: #ffffff;
  }
`

const NameButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 10px 0 7px;
  border: 0;
  border-radius: 12px;
  background: ${({ $open }) => ($open ? '#f2f4f6' : 'transparent')};
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  line-height: inherit;
  color: #191f28;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f2f4f6;
  }
`

const AvatarCircle = styled.span<{ $size: number }>`
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: #eef2ff;
  color: #003399;
  display: grid;
  place-items: center;
`

const AccountLink = styled(Link)<{ $on?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  background: ${({ $on }) => ($on ? '#eef2ff' : 'transparent')};
  color: ${({ $on }) => ($on ? '#003399' : '#333d4b')};
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  white-space: nowrap;
  transition: background-color 0.2s;

  &:hover {
    background: #f2f4f6;
  }
`

const Mega = styled.div<{ $open: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 30;
  background: #ffffff;
  border-bottom: 1px solid #e5e8eb;
  box-shadow: 0 28px 40px -28px rgba(12, 32, 73, 0.35);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-8px)')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease, transform 0.25s ease, visibility 0.2s;
`

const MegaInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 28px clamp(20px, 3.4vw, 40px) 32px;
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 40px;
`

const MegaIntro = styled.div`
  padding-top: 6px;
`

const Eyebrow = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #3d5afe;
`

const MegaTitle = styled.p`
  margin: 8px 0 0;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.03em;
`

const OverviewLink = styled(Link)`
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-height: 44px;
  font-size: 15px;
  font-weight: 700;
  color: #003399;
`

const IndustryGrid = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
`

const IndustryLink = styled(Link)<{ $on?: boolean }>`
  box-sizing: content-box;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 8px 12px;
  border-radius: 14px;
  background: ${({ $on }) => ($on ? '#eef2ff' : 'transparent')};
  color: ${({ $on }) => ($on ? '#003399' : '#191f28')};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  transition: background-color 0.2s;

  img {
    flex: none;
    width: 44px;
    height: 44px;
    object-fit: contain;
  }

  &:hover {
    background: #f2f4f6;
  }
`

const ServiceGrid = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
`

const ServiceLink = styled(Link)<{ $on?: boolean }>`
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  min-height: 120px;
  padding: 18px 18px 16px;
  border-radius: 16px;
  background: ${({ $on }) => ($on ? '#eef2ff' : '#f7f8fa')};
  color: ${({ $on }) => ($on ? '#003399' : '#191f28')};
  transition: background-color 0.2s;

  &:hover {
    background: #eef2ff;
  }
`

const ServiceTop = styled.span`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const IconBox = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 14px -8px rgba(12, 32, 73, 0.35);
  display: grid;
  place-items: center;
  color: #003399;
`

const Num = styled.span`
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #6b7684;
`

const ServiceName = styled.span`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
`

const ServiceLabel = styled.span`
  font-size: 17px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.02em;
`

const ServiceChevron = styled.svg`
  flex: none;
  margin-bottom: 3px;
`

const Hamburger = styled.button`
  margin-left: auto;
  width: 48px;
  height: 48px;
  display: none;
  place-items: center;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #191f28;
  font-family: inherit;
  line-height: inherit;
  cursor: pointer;

  @media (max-width: 1260px) {
    display: grid;
  }
`

const MobilePanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 30;
  max-height: calc(100vh - 69px);
  overflow-y: auto;
  background: #ffffff;
  border-bottom: 1px solid #e5e8eb;
  box-shadow: 0 28px 40px -28px rgba(12, 32, 73, 0.35);
  padding: 4px clamp(20px, 3.4vw, 40px) 20px;
`

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;

  ${LoginButton} {
    margin-top: 16px;
    min-height: 52px;
    font-size: 16px;
  }
`

const MobileLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  border-bottom: 1px solid #f2f4f6;
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  color: ${({ $active }) => ($active ? '#003399' : '#333d4b')};
`

const SubButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #f2f4f6;
  background: none;
  font-family: inherit;
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 800 : 600)};
  line-height: inherit;
  color: ${({ $active }) => ($active ? '#003399' : '#333d4b')};
  text-align: left;
  cursor: pointer;
`

const MobileNameButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 64px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #f2f4f6;
  background: none;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  line-height: inherit;
  color: #191f28;
  text-align: left;
  cursor: pointer;
`

const NameRow = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ChipGrid = styled.ul`
  margin: 0;
  padding: 10px 0 14px;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 4px;
  border-bottom: 1px solid #f2f4f6;
`

const ChipItem = styled.li`
  display: flex;
`

const ChipLink = styled(Link)<{ $on?: boolean; $fill?: string }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 4px 10px;
  border-radius: 12px;
  background: ${({ $on, $fill }) => ($on ? '#eef2ff' : $fill || 'transparent')};
  font-size: 15px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#003399' : '#191f28')};

  img {
    flex: none;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
`

const MobileAccount = styled.ul`
  margin: 0;
  padding: 8px 0 12px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #f2f4f6;
`

const MobileAccountLink = styled(Link)<{ $on?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 12px;
  background: ${({ $on }) => ($on ? '#eef2ff' : 'transparent')};
  color: ${({ $on }) => ($on ? '#003399' : '#333d4b')};
  font-size: 16px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
`
